import { useState, useCallback, useRef, createContext, useContext, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import clsx from 'clsx';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: number;
  type: ToastType;
  message: string;
  actions?: ToastAction[];
  duration?: number;
  onExpire?: () => void;
}

interface ToastContextType {
  toast: (type: ToastType, message: string) => void;
  toastWithUndo: (message: string, onUndo: () => void, onExpire: () => void, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType>({ toast: () => {}, toastWithUndo: () => {} });

let nextId = 0;

const icons: Record<ToastType, ReactNode> = {
  success: <CheckCircle2 className="h-5 w-5 text-success" />,
  error: <XCircle className="h-5 w-5 text-danger" />,
  warning: <AlertTriangle className="h-5 w-5 text-warning" />,
  info: <Info className="h-5 w-5 text-info" />,
};

const bgStyles: Record<ToastType, string> = {
  success: 'border-success/30 bg-success/10',
  error: 'border-danger/30 bg-danger/10',
  warning: 'border-warning/30 bg-warning/10',
  info: 'border-info/30 bg-info/10',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timerRefs = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const removeToast = useCallback((id: number) => {
    const timer = timerRefs.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timerRefs.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((type: ToastType, message: string) => {
    const id = nextId++;
    setToasts((prev) => [...prev, { id, type, message, duration: 3000 }]);
    const timer = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      timerRefs.current.delete(id);
    }, 3000);
    timerRefs.current.set(id, timer);
  }, []);

  const addUndoToast = useCallback((message: string, onUndo: () => void, onExpire: () => void, duration = 5000) => {
    const id = nextId++;
    const expireFn = onExpire;
    setToasts((prev) => [...prev, {
      id,
      type: 'warning' as ToastType,
      message,
      duration,
      onExpire: expireFn,
      actions: [{ label: 'Undo', onClick: () => { onUndo(); removeToast(id); } }],
    }]);
    const timer = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      timerRefs.current.delete(id);
      expireFn();
    }, duration);
    timerRefs.current.set(id, timer);
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ toast: addToast, toastWithUndo: addUndoToast }}>
      {children}
      {createPortal(
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-2 pointer-events-none">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={clsx(
                'pointer-events-auto relative flex items-center gap-3 rounded-lg border px-4 py-3 shadow-lg backdrop-blur-sm animate-in fade-in slide-in-from-top-2',
                bgStyles[t.type],
              )}
            >
              {icons[t.type]}
              <span className="text-sm text-text-primary">{t.message}</span>
              {t.actions?.map((action) => (
                <button
                  key={action.label}
                  onClick={action.onClick}
                  className="ms-1 text-sm font-semibold text-accent underline underline-offset-2 hover:text-accent/80"
                >
                  {action.label}
                </button>
              ))}
              <button
                onClick={() => removeToast(t.id)}
                className="ms-2 text-text-muted hover:text-text-primary"
              >
                <X className="h-4 w-4" />
              </button>
              {/* Countdown bar for undo toasts */}
              {t.actions && t.duration && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 overflow-hidden rounded-b-lg">
                  <div
                    className="h-full bg-accent/60"
                    style={{ animation: `shrink ${t.duration}ms linear forwards` }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>,
        document.body,
      )}
      <style>{`@keyframes shrink { from { width: 100%; } to { width: 0%; } }`}</style>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
